import { beginMapEdit } from './editlock';
// The finish affordance for anything drawn as a series of clicked points.
//
// Double-click used to be the ONLY way to end a polygon or a measurement, and
// it does not survive a touchscreen. A double tap is the browser's own
// zoom gesture; MapLibre's `dblclick` needs two taps inside a tight time AND
// distance window, which a finger on a moving map rarely lands. What actually
// happened on a phone was two extra vertices and a zoom, with no way to close
// the shape — the area, boundary and measure tools were simply unfinishable.
//
// So the way out became explicit. It is better on a mouse too: double-click on
// a map is genuinely ambiguous with "place two vertices quickly", which is why
// a stray vertex was easy to leave behind. Double-click still works everywhere
// it did before — it is now a shortcut rather than the only door — and Undo
// exists because the old flow had no way to take back a misplaced corner
// short of cancelling the whole shape.

export interface DrawBar {
  /** Called after every vertex change: `count` gates Finish, `readout` is an
   *  optional live value (a running distance, say) shown beside it. */
  update(count: number, readout?: string, canFinish?: boolean): void;
  setHint(hint: string): void;
  setBusy(busy: boolean): void;
  /** Restyle one mode button — its label, or whether it reads as on. */
  setExtra(key: string, patch: { label?: string; on?: boolean; hidden?: boolean }): void;
  close(): void;
}

/** A mode the drawing itself has, rather than an action: snapping on or off,
 *  tracing armed. It belongs on the bar because the bar is already where the
 *  gesture's controls live, and on a phone there is nowhere else for it. */
export interface DrawBarExtra {
  key: string;
  label: string;
  title?: string;
  on?: boolean;
  advanced?: boolean;
  onClick(): void;
}

export interface DrawBarOpts {
  /** What is being drawn, e.g. "Damaged area". */
  title: string;
  surface?: HTMLElement;
  /** Below this, Finish stays disabled — 3 for a ring, 2 for a line. */
  minPoints: number;
  /** Shown while Finish is disabled, e.g. "Click at least 3 corners". */
  minHint: string;
  hint?: string;
  finishLabel?: string;
  /** Enter finishes, Backspace / Ctrl+Z removes the last point. */
  pointShortcuts?: boolean;
  onFinish(): void;
  onUndo(): void;
  onCancel(): void;
  /** Mode toggles, shown left of Undo. */
  extras?: DrawBarExtra[];
}

let current: HTMLElement | null = null;
let releaseCurrent: (() => void) | null = null;

/** Mount the bar. Only one can exist — a second call replaces the first, since
 *  two drawings cannot be in progress at once. */
export function showDrawBar(opts: DrawBarOpts): DrawBar {
  releaseCurrent?.();
  current?.remove();
  const el = document.createElement('div');
  // Marked at creation rather than matched with a sibling selector: both mount
  // on <body>, and the phone layout has to lift the bar over a different thing
  // in each context (the cockpit's tool bar, Studio's date strip).
  // A bar with many mode buttons (the boundary tools) wraps instead of
  // running off both sides of the screen.
  el.className = (document.querySelector('.studio') ? 'drawbar in-studio' : 'drawbar')
    + ((opts.extras?.length ?? 0) > 4 ? ' db-many' : '');
  const extra = (x: DrawBarExtra): string => `<button type="button"
    class="db-btn db-extra${x.on ? ' on' : ''}" data-db="x:${x.key}"
    ${x.on !== undefined ? `aria-pressed="${x.on}"` : ''}
    title="${(x.title ?? '').replace(/"/g, '&quot;')}">${x.label}</button>`;
  el.setAttribute('role', 'region');
  el.setAttribute('aria-label', opts.title);
  el.innerHTML = `
    <div class="db-heading"><span class="db-title"></span>
    <span class="db-count"></span></div>
    <span class="db-hint"></span>
    ${(opts.extras ?? []).filter(x => !x.advanced).map(extra).join('')}
    ${opts.extras?.some(x => x.advanced) ? `<details class="db-options"><summary>Guides & snapping</summary>
      <div>${opts.extras.filter(x => x.advanced).map(extra).join('')}</div></details>` : ''}
    <button type="button" class="db-btn" data-db="undo" title="Remove the last point">↶ Undo</button>
    <button type="button" class="db-btn db-go" data-db="finish">✓ Finish</button>
    <button type="button" class="db-btn" data-db="cancel">✕ Cancel</button>`;
  document.body.appendChild(el);
  current = el;
  const release = opts.surface ? beginMapEdit({ surface: opts.surface, controls: el,
    extraControls: () => [...document.querySelectorAll('.terr-vchip')],
  }) : () => {};
  releaseCurrent = release;

  const titleEl = el.querySelector('.db-title') as HTMLElement;
  const countEl = el.querySelector('.db-count') as HTMLElement;
  const undoBtn = el.querySelector('[data-db="undo"]') as HTMLButtonElement;
  const finishBtn = el.querySelector('[data-db="finish"]') as HTMLButtonElement;
  titleEl.textContent = opts.title;
  const hintEl = el.querySelector('.db-hint') as HTMLElement;
  hintEl.textContent = opts.hint ?? opts.minHint;
  finishBtn.textContent = opts.finishLabel ?? '✓ Finish';
  let closed = false;
  const onKey = (ev: KeyboardEvent): void => {
    if (closed || current !== el || !opts.pointShortcuts || ev.defaultPrevented) return;
    if ((ev.target as HTMLElement)?.closest?.('input, textarea, select, button, summary, [contenteditable="true"]')) return;
    if (ev.key === 'Enter' && !finishBtn.disabled) {
      ev.preventDefault(); opts.onFinish();
    } else if ((ev.key === 'Backspace' || ((ev.ctrlKey || ev.metaKey) && !ev.shiftKey && ev.key.toLowerCase() === 'z')) && !undoBtn.disabled) {
      ev.preventDefault(); opts.onUndo();
    }
  };
  document.addEventListener('keydown', onKey);

  const bar: DrawBar = {
    setBusy(busy) {
      el.querySelectorAll<HTMLButtonElement>('button').forEach(button => {
        if (busy) {
          if (!button.hasAttribute('data-was-disabled')) button.dataset.wasDisabled = String(button.disabled);
          button.disabled = true;
        } else if (button.hasAttribute('data-was-disabled')) {
          button.disabled = button.dataset.wasDisabled === 'true';
          delete button.dataset.wasDisabled;
        }
      });
    },
    setHint(hint) { hintEl.textContent = hint; },
    update(count, readout, canFinish) {
      const enough = canFinish ?? count >= opts.minPoints;
      finishBtn.disabled = !enough;
      undoBtn.disabled = count === 0;
      countEl.textContent = readout ?? (enough
        ? `${count} point${count === 1 ? '' : 's'}`
        : opts.minHint);
      countEl.classList.toggle('short', !enough);
    },
    setExtra(key, patch) {
      const btn = el.querySelector(`[data-db="x:${key}"]`) as HTMLButtonElement | null;
      if (!btn) return;
      if (patch.label !== undefined) btn.textContent = patch.label;
      if (patch.on !== undefined) {
        btn.classList.toggle('on', patch.on);
        btn.setAttribute('aria-pressed', String(patch.on));
      }
      if (patch.hidden !== undefined) btn.hidden = patch.hidden;
    },
    close() {
      closed = true;
      release();
      document.removeEventListener('keydown', onKey);
      el.remove();
      if (current === el) { current = null; releaseCurrent = null; }
    },
  };

  // The bar sits over the map, so a click that reaches the map underneath
  // would drop a vertex exactly where the operator aimed at a button.
  el.addEventListener('click', (ev) => {
    const act = (ev.target as HTMLElement).closest('[data-db]') as HTMLElement | null;
    if (!act) return;
    ev.preventDefault();
    ev.stopPropagation();
    const what = act.dataset.db ?? '';
    if (what.startsWith('x:')) {
      opts.extras?.find((x) => x.key === what.slice(2))?.onClick();
      return;
    }
    if (what === 'finish') opts.onFinish();
    else if (what === 'undo') opts.onUndo();
    else opts.onCancel();
  });
  // Swallow the press as well: MapLibre listens on pointer/mouse events, and
  // stopping only the click still leaves a mousedown reaching the canvas.
  for (const type of ['pointerdown', 'mousedown', 'touchstart']) {
    el.addEventListener(type, (ev) => ev.stopPropagation());
  }

  bar.update(0);
  return bar;
}
